import { generatePath, useNavigate, useParams } from 'react-router-dom'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import healthClaimFormAtom from '@/recoil/healthClaimForm'
import ConfirmModalHook from '@/components/common/ConfirmModal/ConfirmModal.hook'
import { ClaimStatusCodeEnum } from '../../HealthClaimPage/ClaimHandlingTable/types'
import { useUpdateClaimStatus } from '@/services/healthClaim/useUpdateClaimStatus'
import message from '@/libs/message'
import CONSTANT from '@/constant'
import { useState } from 'react'
import { useHealthClaimForm } from '@/contexts/healthClaimForm'
import { useQueryClient } from '@tanstack/react-query'

const SendApproveButtonHook = () => {
  const { claimFormId } = useParams<{ claimFormId?: string }>()
  const healthClaim = useRecoilValue(healthClaimFormAtom)
  const setHealthClaim = useSetRecoilState(healthClaimFormAtom)
  const { setConfirmModal } = ConfirmModalHook()
  const { mutateAsync: updateClaimStatus } = useUpdateClaimStatus()
  const { validateAllForms } = useHealthClaimForm()
  const [isIncompleteModalVisible, setIsIncompleteModalVisible] = useState(false)
  const navigate = useNavigate()

  const REQUIRED_MESSAGE = 'กรุณาระบุ'
  const queryClient = useQueryClient()
  const allowedStatuses = [ClaimStatusCodeEnum.IN_REVIEW, ClaimStatusCodeEnum.DOCUMENTS_REQUESTED]

  const isHideButton = !(
    claimFormId &&
    allowedStatuses.includes(healthClaim.data?.statusCode as ClaimStatusCodeEnum) &&
    !healthClaim.data?.savePvAction
  )

  // Modal confirm approve
  const showSendApproveModal = () => {
    if (!claimFormId) return

    setConfirmModal({
      isVisible: true,
      title: 'ยืนยันส่งอนุมัติจ่ายค่าสินไหมทดแทน',
      bodyText: 'คุณได้ตรวจสอบข้อมูลว่าถูกต้องครบถ้วนแล้ว',
      okText: 'ยืนยัน',
      cancelText: 'ยกเลิก',
      dataTestId: 'send-pending-approve-confirm-information-modal',
      onOkFunction: async () => {
        try {
          await updateClaimStatus({
            claimFormId,
            claimStatus: ClaimStatusCodeEnum.PENDING_APPROVAL,
          }).then(() => {
            // Ensure detail is fresh when navigating
            queryClient.refetchQueries({
              queryKey: ['useGetClaimDetail', healthClaim.data?.claimFormId],
            })
          })
          message.success({ content: 'ส่งอนุมัติสำเร็จ', duration: 10 })
        } catch (error: any) {
          message.error({
            content: 'ส่งอนุมัติไม่สำเร็จ',
            duration: 10,
          })
        } finally {
          setConfirmModal({
            isVisible: false,
            onOkFunction: Function,
          })
        }
      },
    })
  }

  const handleClickApprove = async () => {
    if (!claimFormId) return

    const isValid = await validateAllForms({ overrideErrorMessage: REQUIRED_MESSAGE })

    if (!isValid) {
      setIsIncompleteModalVisible(true)
      setHealthClaim((prev) => ({
        ...prev,
        pendingValidation:
          prev.pendingValidation?.highlightRequiredFields &&
          prev.pendingValidation.errorMessage === REQUIRED_MESSAGE
            ? prev.pendingValidation
            : {
                highlightRequiredFields: true,
                errorMessage: REQUIRED_MESSAGE,
              },
      }))
      return
    }
    showSendApproveModal()
  }

  const handleIncompleteModalClose = () => {
    setIsIncompleteModalVisible(false)
  }

  const handleIncompleteModalEdit = () => {
    setIsIncompleteModalVisible(false)
    if (!claimFormId) return
    const nextPath = CONSTANT.ROUTES.HEALTH_CLAIM_EDIT_PAGE
    const currentPath = CONSTANT.ROUTES.HEALTH_CLAIM_VIEW_PAGE
    navigate(generatePath(nextPath, { claimFormId }), { state: { referrer: currentPath } })
  }

  return {
    handleClickApprove,
    isHideButton,
    isIncompleteModalVisible,
    handleIncompleteModalClose,
    handleIncompleteModalEdit,
  }
}

export default SendApproveButtonHook
